import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import certificatesService from "../services/certificatesService";
import styles from "./Certificates.module.css";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const Certificates = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  const [filters, setFilters] = useState({
    type_id: 0, // 0 = All, numeric IDs matching Laravel
    cert_status: 0, // 0 = All, 1 = Active, 2 = Expired, 3 = Due to expire, 4 = Archived
    property_type: 1, // 0 = All, 1 = Active only
    client_id: null, // Client ID for filtering
    clientSearch: "", // Client name for display
    tableSearch: "",
  });
  
  // Typeahead state for client search
  const [clientTypeaheadResults, setClientTypeaheadResults] = useState([]);
  const [showClientTypeahead, setShowClientTypeahead] = useState(false);

  // Separate state for search input to prevent focus loss during debounce
  const [searchInputValue, setSearchInputValue] = useState("");
  const searchDebounceRef = useRef(null);
  const [dataTableConfig, setDataTableConfig] = useState({
    start: 0,
    length: 30,
    recordsTotal: 0,
    recordsFiltered: 0,
  });

  const [columnVisibility, setColumnVisibility] = useState({
    type: true,
    client: true,
    completed: true,
    expiration: true,
    contractorAppointment: true,
    property: true,
    actions: true,
  });

  const [showColumnVisibility, setShowColumnVisibility] = useState(false);

  // State for table sorting
  const [sortField, setSortField] = useState("type");
  const [sortDirection, setSortDirection] = useState("asc");

  // Mock data for fallback
  const mockCertificates = [
    {
      id: 1,
      type: "Gas Safe Certificate",
      client: '<a href="/clients/1"><span style="font-weight: 700;">Metropolitan Thames Valley (MTVH)</span></a>',
      clientId: 1,
      completed: "15/10/2015",
      expiration: "14/10/2016",
      contractorAppointment: "",
      property: '<a href="/properties/1"><span style="font-weight: 700;">16 Rodgers House</span></a>',
      propertyId: 1,
      status: "active",
      documentId: 101,
    },
    {
      id: 2,
      type: "Gas Safe Certificate",
      client: '<a href="/clients/1"><span style="font-weight: 700;">Metropolitan Thames Valley (MTVH)</span></a>',
      clientId: 1,
      completed: "09/11/2015",
      expiration: "08/11/2016",
      contractorAppointment: "",
      property: '<a href="/properties/2"><span style="font-weight: 700;">7 Rodgers House</span></a>',
      propertyId: 2,
      status: "active",
      documentId: 102,
    },
    {
      id: 3,
      type: "Gas Safe Certificate",
      client: '<a href="/clients/1"><span style="font-weight: 700;">Metropolitan Thames Valley (MTVH)</span></a>',
      clientId: 1,
      completed: "13/10/2016",
      expiration: "13/10/2017",
      contractorAppointment: "",
      property: '<a href="/properties/3"><span style="font-weight: 700;">12 Plummer Road</span></a>',
      propertyId: 3,
      status: "active",
      documentId: 103,
    },
  ];

  // Certificate types with numeric IDs matching Laravel config
  const certificateTypes = [
    { id: 0, name: "All" },
    { id: 6, name: "Gas Safe Certificate" },
    { id: 7, name: "NIC Certificate" },
    { id: 8, name: "Asbestos Register" },
    { id: 9, name: "Fire Risk Assessment" },
    { id: 10, name: "Legionella" },
    { id: 30, name: "Fire Alarm" },
    { id: 31, name: "Emergency Lighting" },
    { id: 32, name: "Fire Extinguisher Certificate" },
    { id: 33, name: "PAT Testing" },
    { id: 29, name: "240V Certificate" },
  ];

  // Certificate type IDs that can be archived/removed manually (matching Laravel typeIdsToArchiveManually)
  // All certificate types can be archived/removed manually in Laravel
  const typeIdsToArchiveManually = [6, 7, 8, 9, 10, 29, 30, 31, 32, 33];

  // Status options with numeric IDs matching Laravel config
  const statusOptions = [
    { id: 0, name: "All" },
    { id: 1, name: "Active" },
    { id: 4, name: "Archived" },
    { id: 3, name: "Due to expire" },
    { id: 2, name: "Expired" },
  ];

  // Property type options with numeric IDs matching Laravel config
  const propertyTypeOptions = [
    { id: 0, name: "All" },
    { id: 1, name: "Active only" },
  ];

  useEffect(() => {
    console.log("🔍 [FRONTEND] Filters changed, reloading certificates:", {
      type_id: filters.type_id,
      cert_status: filters.cert_status,
      property_type: filters.property_type,
      client_id: filters.client_id,
      tableSearch: filters.tableSearch
    });
    loadCertificates();
  }, [filters, dataTableConfig.start, dataTableConfig.length]);

  // Close column visibility dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnVisibility) {
        const container = event.target.closest(`.${styles.columnVisibilityContainer}`);
        const dropdown = event.target.closest(`.${styles.columnVisibilityDropdown}`);
        
        if (!container && !dropdown) {
          setShowColumnVisibility(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility, styles.columnVisibilityContainer, styles.columnVisibilityDropdown]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      
      if (!useMockData) {
        // Prepare request data matching Laravel DataTables format
        const requestData = {
          type_id: filters.type_id,
          cert_status: filters.cert_status,
          property_type: filters.property_type,
          client_id: filters.client_id || null,
          search: { value: filters.tableSearch || "" },
          start: dataTableConfig.start,
          length: dataTableConfig.length,
          draw: 1,
        };

        // Use correct endpoint matching Laravel
        console.log("🔍 [FRONTEND] Calling API with requestData:", requestData);
        console.log("🔍 [FRONTEND] client_id being sent:", requestData.client_id);
        console.log("🔍 [FRONTEND] clientSearch display value:", filters.clientSearch);
        const response = await certificatesService.getClientCertificates(requestData);
        console.log("🔍 [FRONTEND] API Response:", response);
        console.log("🔍 [FRONTEND] Response data length:", response.data?.length || 0);
        
        // Handle DataTables response format
        if (response.draw !== undefined) {
          // DataTables format
          console.log("🔍 [FRONTEND] Using DataTables format, setting", response.data?.length || 0, "certificates");
          setCertificates(response.data || []);
          setDataTableConfig((prev) => ({
            ...prev,
            recordsTotal: response.recordsTotal || 0,
            recordsFiltered: response.recordsFiltered || 0,
          }));
        } else if (response.success) {
          // Success format fallback
          console.log("🔍 [FRONTEND] Using success format, setting", response.data?.length || 0, "certificates");
          setCertificates(response.data || []);
          setDataTableConfig((prev) => ({
            ...prev,
            recordsTotal: response.recordsTotal || response.data?.length || 0,
            recordsFiltered: response.recordsFiltered || response.data?.length || 0,
          }));
        } else {
          console.error("🔍 [FRONTEND] API response format not recognized:", response);
          throw new Error("API failed");
        }
      } else {
        // Use mock data with server-side pagination
        const filteredData = filterMockData(mockCertificates);
        const totalFiltered = filteredData.length;
        
        // Apply server-side pagination to mock data
        const start = dataTableConfig.start;
        const length = dataTableConfig.length;
        const paginatedData = filteredData.slice(start, start + length);
        
        console.log("🔍 [FRONTEND] Mock data pagination:", {
          total: totalFiltered,
          start: start,
          length: length,
          paginated: paginatedData.length
        });
        
        setCertificates(paginatedData);
        setDataTableConfig((prev) => ({
          ...prev,
          recordsTotal: mockCertificates.length,
          recordsFiltered: totalFiltered,
        }));
      }
    } catch (error) {
      console.error("Error loading certificates:", error);
      if (!useMockData) {
        setUseMockData(true);
        toast.info("Switched to mock data due to API failure");
        loadCertificates(); // Retry with mock data
      } else {
        toast.error("Failed to load certificates");
      }
    } finally {
      setLoading(false);
    }
  };

  const filterMockData = (data) => {
    let filtered = [...data];

    // Filter by certificate type (numeric ID)
    if (filters.type_id !== 0) {
      // Map numeric ID to certificate type name for mock filtering
      const typeMap = {
        6: "Gas Safe Certificate",
        7: "NIC Certificate",
        8: "Asbestos Register",
        9: "Fire Risk Assessment",
        10: "Legionella",
        30: "Fire Alarm",
        31: "Emergency Lighting",
        32: "Fire Extinguisher Certificate",
        33: "PAT Testing",
        29: "240V Certificate",
      };
      const typeName = typeMap[filters.type_id];
      if (typeName) {
        filtered = filtered.filter((cert) =>
          cert.type.toLowerCase().includes(typeName.toLowerCase())
        );
      }
    }

    // Filter by status (numeric ID)
    if (filters.cert_status !== 0) {
      const statusMap = {
        1: "active",
        2: "expired",
        3: "due_to_expire",
        4: "archived",
      };
      const statusValue = statusMap[filters.cert_status];
      if (statusValue) {
        filtered = filtered.filter((cert) => cert.status === statusValue);
      }
    }

    // Filter by client search
    if (filters.clientSearch) {
      filtered = filtered.filter((cert) =>
        cert.client.toLowerCase().includes(filters.clientSearch.toLowerCase())
      );
    }

    // Filter by table search
    if (filters.tableSearch) {
      const searchTerm = filters.tableSearch.toLowerCase();
      filtered = filtered.filter(
        (cert) =>
          cert.type.toLowerCase().includes(searchTerm) ||
          cert.client.toLowerCase().includes(searchTerm) ||
          cert.property.toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  };
  
  // Handle client search for typeahead
  const handleClientSearch = async (value) => {
    setFilters((prev) => ({
      ...prev,
      clientSearch: value,
      client_id: null, // Clear ID when name changes
    }));

    if (value.length >= 1) {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${backendUrl}/api/clients/ajax/${encodeURIComponent(value)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        if (data.success && data.data) {
          setClientTypeaheadResults(data.data);
          setShowClientTypeahead(true);
        } else {
          setClientTypeaheadResults([]);
          setShowClientTypeahead(false);
        }
      } catch (error) {
        console.error('Error searching clients:', error);
        setClientTypeaheadResults([]);
        setShowClientTypeahead(false);
      }
    } else {
      setClientTypeaheadResults([]);
      setShowClientTypeahead(false);
    }
  };

  // Handle client selection from typeahead
  const handleClientSelect = (client, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log("🔍 [FRONTEND] Client selected:", client);
    console.log("🔍 [FRONTEND] Setting client_id to:", client.id);
    console.log("🔍 [FRONTEND] Setting clientSearch to:", client.name || client.company || "");
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        client_id: client.id,
        clientSearch: client.name || client.company || ""
      };
      console.log("🔍 [FRONTEND] Updated filters with client:", newFilters);
      return newFilters;
    });
    setShowClientTypeahead(false);
    // Reset pagination to first page when client filter changes
    setDataTableConfig((prev) => ({
      ...prev,
      start: 0,
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setDataTableConfig((prev) => ({
      ...prev,
      start: 0,
    }));
  };

  // Debounced search handler - only triggers API call after user stops typing
  const handleSearchChange = useCallback((value) => {
    // Update input value immediately (no focus loss)
    setSearchInputValue(value);

    // Clear any existing debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new debounce timer - only update filters after 500ms of no typing
    searchDebounceRef.current = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        tableSearch: value,
      }));
      setDataTableConfig((prev) => ({
        ...prev,
        start: 0,
      }));
    }, 500);
  }, []);

  const handlePageChange = (newStart) => {
    setDataTableConfig((prev) => ({
      ...prev,
      start: newStart,
    }));
    // Reload data with new pagination (will be triggered by useEffect)
  };

  const handlePageSizeChange = (newLength) => {
    setDataTableConfig((prev) => ({
      ...prev,
      start: 0, // Reset to first page when changing page size
      length: newLength,
    }));
    // Reload data with new pagination (will be triggered by useEffect)
  };

  const exportCSV = async () => {
    try {
      if (!useMockData) {
      const response = await certificatesService.exportCSV(filters);
        const blob = new Blob([response], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "certificates.csv";
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // Mock CSV export
        const csvContent = generateMockCSV();
        const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
      a.href = url;
        a.download = "certificates.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      }
      toast.success("CSV exported successfully");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    }
  };

  const generateMockCSV = () => {
    const headers = [
      "Type",
      "Client",
      "Completed",
      "Expiration",
      "Contractor Appointment",
      "Property",
    ];
    const rows = mockCertificates.map((cert) => [
      cert.type,
      cert.client,
      cert.completed,
      cert.expiration,
      cert.contractorAppointment,
      cert.property,
    ]);

    return [headers, ...rows].map((row) => row.join(",")).join("\n");
  };

  const exportPDF = async () => {
    try {
      if (!useMockData) {
      const blob = await certificatesService.exportPDF(filters);
      const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
      link.href = url;
        link.download = "certificates.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      } else {
        // Mock PDF export - just show success message
        toast.success("PDF export simulation completed");
      }
      toast.success("PDF exported successfully");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  const printTable = () => {
    window.print();
  };

  const toggleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumn = (columnKey) => {
    console.log('Toggling column:', columnKey);
    setColumnVisibility(prev => {
      const newState = {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
      console.log('New column visibility state:', newState);
      return newState;
    });
  };

  const handleDownload = async (documentId) => {
    try {
      if (!useMockData) {
        // Get download URL or blob from backend
        const response = await certificatesService.downloadCertificate(documentId);
        
        if (response.success && response.downloadUrl) {
          // Case 1: S3 presigned URL - fetch as blob first to avoid corruption
          // Use filename from response (ensures .pdf extension) or fallback
          const filename = response.filename || `certificate_${documentId}.pdf`;
          // Ensure filename has .pdf extension
          const finalFilename = filename.toLowerCase().endsWith('.pdf') 
            ? filename 
            : `${filename}.pdf`;
          
          console.log(`📥 Fetching certificate from presigned URL: ${response.downloadUrl.substring(0, 50)}...`);
          
          // Fetch file as blob to ensure proper binary data handling (works for cross-origin URLs)
          const fetchResponse = await fetch(response.downloadUrl);
          
          if (!fetchResponse.ok) {
            throw new Error(`Failed to fetch certificate: ${fetchResponse.status} ${fetchResponse.statusText}`);
          }
          
          const blob = await fetchResponse.blob();
          
          // Validate blob
          if (!blob || blob.size === 0) {
            throw new Error("Downloaded certificate blob is empty");
          }
          
          // Ensure blob has correct MIME type
          const pdfBlob = blob.type === 'application/pdf' 
            ? blob 
            : new Blob([blob], { type: 'application/pdf' });
          
          console.log(`✅ Certificate blob received: ${pdfBlob.size} bytes, type: ${pdfBlob.type}`);
          
          // Create blob URL and download link
          const blobUrl = window.URL.createObjectURL(pdfBlob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = finalFilename;
          document.body.appendChild(link);
          link.click();
          
          // Cleanup
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          }, 100);
          
          toast.success("Certificate downloaded successfully");
        } else if (response instanceof Blob) {
          // Case 2: PDF blob (generated PDF) - create download link
          // Validate blob before creating URL
          if (response.size === 0) {
            throw new Error("PDF blob is empty");
          }
          
          // Ensure blob has correct MIME type
          const blob = response.type === 'application/pdf' 
            ? response 
            : new Blob([response], { type: 'application/pdf' });
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          // Always ensure .pdf extension
          link.download = `certificate_${documentId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Revoke URL after a short delay to ensure download starts
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 100);
          
          toast.success("Certificate downloaded successfully");
        } else {
          console.error("Invalid response type:", typeof response, response);
          throw new Error("Invalid response from server - expected Blob or presigned URL");
        }
      } else {
        // Mock download
        toast.success("Certificate download simulation completed");
      }
    } catch (error) {
      console.error("Error downloading certificate:", error);
      toast.error("Failed to download certificate");
    }
  };

  const handleEdit = (propertyId, documentId) => {
    // Use singular "certificate" to match Laravel route
    navigate(`/properties/${propertyId}/certificate/${documentId}/edit`);
  };
  
  const handleArchive = async (documentId) => {
    try {
      if (!useMockData) {
        const response = await certificatesService.archiveCertificate(documentId);
        if (response.success) {
          toast.success("Certificate successfully archived");
          loadCertificates();
        } else {
          toast.error("Failed to archive certificate");
        }
      } else {
        // Mock archive
        setCertificates((prev) =>
          prev.map((cert) =>
            cert.documentId === documentId
              ? { ...cert, status: "archived" }
              : cert
          )
        );
        toast.success("Certificate successfully archived (mock)");
      }
    } catch (error) {
      console.error("Error archiving certificate:", error);
      toast.error("Failed to archive certificate");
    }
  };

  const handleDelete = async (propertyId, documentId) => {
    if (window.confirm("Are you sure you want to remove this certificate?")) {
      try {
        if (!useMockData) {
          const response = await certificatesService.deleteCertificate(
            propertyId,
            documentId
          );
        if (response.success) {
            toast.success("Certificate successfully deleted");
            loadCertificates();
          } else {
            toast.error("Failed to delete certificate");
          }
        } else {
          // Mock delete
          setCertificates((prev) =>
            prev.filter((cert) => cert.documentId !== documentId)
          );
          toast.success("Certificate successfully deleted (mock)");
        }
      } catch (error) {
        console.error("Error deleting certificate:", error);
        toast.error("Failed to delete certificate");
      }
    }
  };

  const handleActivate = async (documentId) => {
    try {
      if (!useMockData) {
        const response = await certificatesService.activateCertificate(
          documentId
        );
      if (response.success) {
          toast.success("Certificate successfully activated");
          loadCertificates();
        } else {
          toast.error("Failed to activate certificate");
        }
      } else {
        // Mock activate
        setCertificates((prev) =>
          prev.map((cert) =>
            cert.documentId === documentId
              ? { ...cert, status: "active" }
              : cert
          )
        );
        toast.success("Certificate successfully activated (mock)");
      }
    } catch (error) {
      console.error("Error activating certificate:", error);
      toast.error("Failed to activate certificate");
    }
  };

  // Handle table sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const navigateToClient = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const navigateToProperty = (propertyId) => {
    navigate(`/properties/${propertyId}`);
  };

  // Sort certificates
  const sortedCertificates = React.useMemo(() => {
    return [...certificates].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (sortField === "type") {
        aValue = a.type || "";
        bValue = b.type || "";
      } else if (sortField === "client") {
        // Extract client name from HTML string
        const aClientMatch = a.client?.match(/<span[^>]*>([^<]+)<\/span>/);
        const bClientMatch = b.client?.match(/<span[^>]*>([^<]+)<\/span>/);
        aValue = aClientMatch ? aClientMatch[1] : "";
        bValue = bClientMatch ? bClientMatch[1] : "";
      } else if (sortField === "completed") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else if (sortField === "expiration") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else if (sortField === "contractorAppointment") {
        aValue = a.contractorAppointment || "";
        bValue = b.contractorAppointment || "";
      } else if (sortField === "property") {
        // Extract property name from HTML string
        const aPropertyMatch = a.property?.match(/<span[^>]*>([^<]+)<\/span>/);
        const bPropertyMatch = b.property?.match(/<span[^>]*>([^<]+)<\/span>/);
        aValue = aPropertyMatch ? aPropertyMatch[1] : "";
        bValue = bPropertyMatch ? bPropertyMatch[1] : "";
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [certificates, sortField, sortDirection]);

  // Debug log for column visibility state
  console.log('Current column visibility state:', columnVisibility);

  if (loading && certificates.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <nav className={styles.breadcrumb}>
        <ol>
          <li>
            <a href="/dashboard">Dashboard</a>
          </li>
          <li>
            <a href="/clients">Clients</a>
          </li>
          <li className={styles.active}>Certificates</li>
        </ol>
      </nav>

      {/* Content Area with Background */}
      <div className={styles.contentArea}>
        {/* Page Title */}

        {/* Filters Section */}
        <div className={styles.filtersSection}>
        {/* Certificate Type Filter */}
        <div className={styles.filterRow}>
          <h3>Certificates</h3>

          {/* <label className={styles.filterLabel}>Certificate</label> */}
          <div className={styles.radioGroup}>
            {certificateTypes.map((type) => (
              <label key={type.id} className={styles.radioLabel}>
                      <input
                        type="radio"
                  name="type_id"
                        value={type.id}
                  checked={filters.type_id === type.id}
                  onChange={(e) =>
                    handleFilterChange("type_id", parseInt(e.target.value))
                  }
                />
                <span className={styles.radioText}>{type.name}</span>
                    </label>
                  ))}
          </div>
        </div>

        {/* Status Filter */}
        <div className={styles.filterRow}>
          <label className={styles.filterLabel}>Status</label>
          <div className={styles.radioGroup}>
            {statusOptions.map((status) => (
              <label key={status.id} className={styles.radioLabel}>
                      <input
                        type="radio"
                  name="cert_status"
                        value={status.id}
                  checked={filters.cert_status === status.id}
                  onChange={(e) => handleFilterChange("cert_status", parseInt(e.target.value))}
                      />
                <span className={styles.radioText}>{status.name}</span>
                    </label>
                  ))}
          </div>
        </div>

        {/* Property Type Filter */}
        <div className={styles.filterRow}>
          <label className={styles.filterLabel}>Property Type</label>
          <div className={styles.radioGroup}>
            {propertyTypeOptions.map((type) => (
              <label key={type.id} className={styles.radioLabel}>
                      <input
                        type="radio"
                  name="property_type"
                        value={type.id}
                  checked={filters.property_type === type.id}
                  onChange={(e) =>
                    handleFilterChange("property_type", parseInt(e.target.value))
                  }
                />
                <span className={styles.radioText}>{type.name}</span>
                    </label>
                  ))}
                </div>
                    </div>

        {/* Client Search with Typeahead */}
        <div className={styles.clientSearchRow}>
          <label className={styles.clientLabel}>Client</label>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              className={`${styles.clientInput} typeahead js-client-field-typeahead`}
              placeholder="Type client name"
              value={filters.clientSearch}
              onChange={(e) => handleClientSearch(e.target.value)}
              onBlur={(e) => {
                // Only close dropdown if clicking outside (not on dropdown item)
                setTimeout(() => {
                  // Check if the related target is not a dropdown item
                  if (!e.relatedTarget || !e.relatedTarget.closest('[data-client-dropdown]')) {
                    setShowClientTypeahead(false);
                  }
                }, 200);
              }}
              autoComplete="off"
              style={{ border: '1px solid #0a7280', width: '100%' }}
            />
            <input type="hidden" name="client_id" value={filters.client_id || ''} />
            {showClientTypeahead && clientTypeaheadResults.length > 0 && (
              <div 
                data-client-dropdown
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderTop: 'none',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseDown={(e) => e.preventDefault()} // Prevent input blur when clicking dropdown
              >
                {clientTypeaheadResults.map((client) => (
                  <div
                    key={client.id}
                    data-client-dropdown
                    onClick={(e) => handleClientSelect(client, e)}
                    onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <strong>{client.name || client.company}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className={styles.searchButton}
            onClick={() => loadCertificates()}
          >
            Search
          </button>
        </div>
          </div>

      {/* Table Controls */}
      <div className={styles.tableControls}>
        <div className={styles.leftControls}>
          <div className={styles.entriesControl}>
                  <label>
              Show{" "}
                    <select
                      value={dataTableConfig.length}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className={styles.entriesSelect}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                      <option value={75}>75</option>
                      <option value={100}>100</option>
              </select>{" "}
                    entries
                  </label>
                </div>
          <div className={styles.tableSearch}>
                  <label>
              Search:{" "}
                    <input
                      type="search"
                className={styles.searchInput}
                value={searchInputValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search certificates..."
                    />
                  </label>
              </div>
            </div>

            {/* Export Buttons */}
        <div className={styles.exportButtons}>
                  <button
                    type="button"
            className={styles.exportButton}
                    onClick={exportCSV}
                  >
            Export as CSV
                  </button>
                  <button
                    type="button"
            className={styles.exportButton}
                    onClick={exportPDF}
                  >
            Export as PDF
                  </button>
                  <button
                    type="button"
            className={styles.exportButton}
                    onClick={printTable}
                  >
            Print table
                  </button>
                  <div className={styles.columnVisibilityContainer}>
                    <button
                      type="button"
                      className={styles.exportButton}
                      onClick={toggleColumnVisibility}
                    >
                      Column visibility
                    </button>
                    {showColumnVisibility && (
                      <div 
                        className={styles.columnVisibilityDropdown}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={styles.columnVisibilityHeader}>
                          <span>Toggle columns:</span>
                        </div>
                        <div className={styles.columnVisibilityOptions}>
                          <label className={styles.columnVisibilityOption}>
                            <input
                              type="checkbox"
                              checked={columnVisibility.type}
                              onChange={() => toggleColumn('type')}
                            />
                            <span>Type</span>
                          </label>
                          <label className={styles.columnVisibilityOption}>
                            <input
                              type="checkbox"
                              checked={columnVisibility.client}
                              onChange={() => toggleColumn('client')}
                            />
                            <span>Client</span>
                          </label>
                          <label className={styles.columnVisibilityOption}>
                            <input
                              type="checkbox"
                              checked={columnVisibility.completed}
                              onChange={() => toggleColumn('completed')}
                            />
                            <span>Completed</span>
                          </label>
                          <label className={styles.columnVisibilityOption}>
                            <input
                              type="checkbox"
                              checked={columnVisibility.expiration}
                              onChange={() => toggleColumn('expiration')}
                            />
                            <span>Expiration</span>
                          </label>
                          <label className={styles.columnVisibilityOption}>
                            <input
                              type="checkbox"
                              checked={columnVisibility.contractorAppointment}
                              onChange={() => toggleColumn('contractorAppointment')}
                            />
                            <span>Contractor Appointment</span>
                          </label>
                          <label className={styles.columnVisibilityOption}>
                            <input
                              type="checkbox"
                              checked={columnVisibility.property}
                              onChange={() => toggleColumn('property')}
                            />
                            <span>Property</span>
                          </label>
                          <label className={styles.columnVisibilityOption}>
                            <input
                              type="checkbox"
                              checked={columnVisibility.actions}
                              onChange={() => toggleColumn('actions')}
                            />
                            <span>Actions</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
              </div>
            </div>

            {/* Certificates Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.certificatesTable}>
                  <thead>
                    <tr>
                      {columnVisibility.type && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("type")}
                          style={{ cursor: "pointer" }}
                        >
                          Type<SortIcon direction={sortField === "type" ? sortDirection : null} isActive={sortField === "type"} />
                        </th>
                      )}
                      {columnVisibility.client && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("client")}
                          style={{ cursor: "pointer" }}
                        >
                          Client<SortIcon direction={sortField === "client" ? sortDirection : null} isActive={sortField === "client"} />
                        </th>
                      )}
                      {columnVisibility.completed && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("completed")}
                          style={{ cursor: "pointer" }}
                        >
                          Completed<SortIcon direction={sortField === "completed" ? sortDirection : null} isActive={sortField === "completed"} />
                        </th>
                      )}
                      {columnVisibility.expiration && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("expiration")}
                          style={{ cursor: "pointer" }}
                        >
                          Expiration<SortIcon direction={sortField === "expiration" ? sortDirection : null} isActive={sortField === "expiration"} />
                        </th>
                      )}
                      {columnVisibility.contractorAppointment && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("contractorAppointment")}
                          style={{ cursor: "pointer" }}
                        >
                          Contractor Appointment<SortIcon direction={sortField === "contractorAppointment" ? sortDirection : null} isActive={sortField === "contractorAppointment"} />
                        </th>
                      )}
                      {columnVisibility.property && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("property")}
                          style={{ cursor: "pointer" }}
                        >
                          Property<SortIcon direction={sortField === "property" ? sortDirection : null} isActive={sortField === "property"} />
                        </th>
                      )}
                      {columnVisibility.actions && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
            {sortedCertificates.map((certificate) => (
              <tr key={certificate.id}>
                        {columnVisibility.type && <td>{certificate.type}</td>}
                {columnVisibility.client && (
                  <td>
                    <div 
                      dangerouslySetInnerHTML={{ __html: certificate.client }} 
                      onClick={(e) => {
                        if (e.target.tagName === 'A') {
                          e.preventDefault();
                          navigateToClient(certificate.clientId);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                )}
                {columnVisibility.completed && <td>{certificate.completed && certificate.completed !== "Invalid Date" ? certificate.completed : "-"}</td>}
                {columnVisibility.expiration && <td>{certificate.expiration && certificate.expiration !== "Invalid Date" ? certificate.expiration : "-"}</td>}
                {columnVisibility.contractorAppointment && <td>{certificate.contractorAppointment && certificate.contractorAppointment !== "Invalid Date" ? certificate.contractorAppointment : "-"}</td>}
                {columnVisibility.property && (
                  <td>
                    <div 
                      dangerouslySetInnerHTML={{ __html: certificate.property }} 
                      onClick={(e) => {
                        if (e.target.tagName === 'A') {
                          e.preventDefault();
                          navigateToProperty(certificate.propertyId);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                )}
                {columnVisibility.actions && (
                  <td className={styles.actionsCell}>
                    <button
                      className={`${styles.actionButton} ${styles.downloadButton}`}
                      onClick={() => handleDownload(certificate.documentId)}
                      title="Download"
                    >
                      Download
                    </button>
                    {/* Edit button - always shown (matching Laravel) */}
                    <button
                      className={`${styles.actionButton} ${styles.editButton}`}
                      onClick={() =>
                        handleEdit(certificate.propertyId, certificate.documentId)
                      }
                      title="Edit"
                    >
                      Edit
                    </button>
                    {/* Archive button - only shown for certificate types that can be archived manually (matching Laravel) */}
                    {certificate.type_id && typeIdsToArchiveManually.includes(certificate.type_id) && 
                     (certificate.status === "active" || certificate.status === 1) && (
                      <button
                        className={`${styles.actionButton} ${styles.archiveButton}`}
                        onClick={() => handleArchive(certificate.documentId)}
                        title="Archive"
                      >
                        Archive
                      </button>
                    )}
                    {/* Active button - only shown for archived certificates of types that can be archived manually (matching Laravel buttons.blade.php) */}
                    {certificate.type_id && typeIdsToArchiveManually.includes(certificate.type_id) && 
                     (certificate.status === "archived" || certificate.status === 0) && (
                      <button
                        className={`${styles.actionButton} ${styles.activeButton}`}
                        onClick={() => handleActivate(certificate.documentId)}
                        title="Active"
                      >
                        Active
                      </button>
                    )}
                    {/* Remove button - always shown for all certificate types (matching Laravel) */}
                    <button
                      className={`${styles.actionButton} ${styles.removeButton}`}
                      onClick={() =>
                        handleDelete(
                          certificate.propertyId,
                          certificate.documentId
                        )
                      }
                      title="Remove"
                    >
                      Remove
                    </button>
                  </td>
                )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            {/* Server-Side Pagination */}
            {dataTableConfig.recordsFiltered >= 0 && (
        <div className={styles.paginationWrapper}>
          <div className={styles.paginationInfo}>
            {dataTableConfig.recordsFiltered > 0 ? (
              <>
                Showing {dataTableConfig.start + 1} to{" "}
                {Math.min(
                  dataTableConfig.start + dataTableConfig.length,
                  dataTableConfig.recordsFiltered
                )}{" "}
                of {dataTableConfig.recordsFiltered} entries
                {dataTableConfig.recordsTotal !== dataTableConfig.recordsFiltered && (
                  <span className={styles.totalInfo}>
                    {" "}(Total: {dataTableConfig.recordsTotal})
                  </span>
                )}
              </>
            ) : (
              <>No entries found</>
            )}
          </div>
          <div className={styles.pagination}>
            {/* Previous Button */}
            <button
              className={`${styles.paginationButton} ${
                dataTableConfig.start === 0 ? styles.disabled : ""
              }`}
              onClick={() =>
                handlePageChange(
                  Math.max(0, dataTableConfig.start - dataTableConfig.length)
                )
              }
              disabled={dataTableConfig.start === 0}
            >
              Previous
            </button>
            
            {/* Page Numbers with Ellipsis */}
            {dataTableConfig.recordsFiltered > 0 && (() => {
              const totalPages = Math.ceil(
                dataTableConfig.recordsFiltered / dataTableConfig.length
              );
              const currentPage = Math.floor(dataTableConfig.start / dataTableConfig.length) + 1;
              const pages = [];
              
              // Show first page
              if (totalPages > 0) {
                pages.push(1);
              }
              
              // Show ellipsis if current page is far from start
              if (currentPage > 3 && totalPages > 5) {
                pages.push("ellipsis-start");
              }
              
              // Show pages around current page
              const startPage = Math.max(2, currentPage - 1);
              const endPage = Math.min(totalPages - 1, currentPage + 1);
              
              for (let i = startPage; i <= endPage; i++) {
                if (i !== 1 && i !== totalPages) {
                  pages.push(i);
                }
              }
              
              // Show ellipsis if current page is far from end
              if (currentPage < totalPages - 2 && totalPages > 5) {
                pages.push("ellipsis-end");
              }
              
              // Show last page
              if (totalPages > 1) {
                pages.push(totalPages);
              }
              
              return pages.map((page, index) => {
                if (page === "ellipsis-start" || page === "ellipsis-end") {
                  return (
                    <span key={`ellipsis-${index}`} className={styles.paginationEllipsis}>
                      ...
                    </span>
                  );
                }
                
                const pageStart = (page - 1) * dataTableConfig.length;
                const isCurrentPage = pageStart === dataTableConfig.start;
                
                return (
                  <button
                    key={page}
                    className={`${styles.paginationButton} ${
                      isCurrentPage ? styles.active : ""
                    }`}
                    onClick={() => handlePageChange(pageStart)}
                  >
                    {page}
                  </button>
                );
              });
            })()}
            
            {/* Next Button */}
            <button
              className={`${styles.paginationButton} ${
                dataTableConfig.start + dataTableConfig.length >=
                dataTableConfig.recordsFiltered
                  ? styles.disabled
                  : ""
              }`}
              onClick={() =>
                handlePageChange(dataTableConfig.start + dataTableConfig.length)
              }
              disabled={
                dataTableConfig.start + dataTableConfig.length >=
                dataTableConfig.recordsFiltered
              }
            >
              Next
            </button>
          </div>
        </div>
      )}

      {certificates.length === 0 && !loading && (
        <div className={styles.noData}>
          <p>No certificates found!</p>
        </div>
      )}
      </div>
    </div>
  );
};

export default Certificates; 
